<template>
  <div class="container p-0 mt-2" v-show="!hidden">
    <!-- role=group met aria-labelledby naar het label dat SFormElement rendert:
         de groep krijgt zo een naam zonder dat de kolomindeling verandert.
         `required` stond hier op een <div>, waar het niets betekent. -->
    <div class="row" role="group" :aria-labelledby="ids.label" :aria-describedby="describedBy">
      <div class="form-group" :class="{ 'was-validated': wasValidated }">
        <div v-for="(option,index) in selectOptions" :key="index" class="col-auto">
          <div class="form-check form-check-inline">
            <!-- Een gedeelde `name` maakt de groep herkenbaar als één veld.
                 Zonder dit ziet de foutensamenvatting vijf losse fouten waar er
                 één is ("Isolatie", "Warmtepomp", ...) en telt hij ze ook als
                 vijf. De radiogroep had die naam al; hier ontbrak hij. -->
            <input @change="checkboxChanged()"
                   class="form-check-input"
                   type="checkbox"
                   :id="ids.option(index)"
                   :name="element.name"
                   v-model="elData[option.name]"
                   :required="isRequired"
                   :disabled="hidden">
            <label class="form-check-label" :for="ids.option(index)">{{getTextFor(option.text,lang)}}</label>
          </div>
        </div>
        <div v-if="isLoading" class="input-group-append">
          <span class="input-group-text">
            <div class="spinner-border spinner-border-sm" role="status">
              <span class="visually-hidden">Laden...</span>
            </div>
          </span>
        </div>
        <!-- Eén foutmelding per groep. Hij stond in de v-for, dus bij vijf
             opties verschenen er vijf identieke meldingen. -->
        <div class="invalid-feedback" :id="ids.error" :hidden="!ongeldig">
          {{ getTextFor(element.invalid, lang) || 'Maak een keuze.' }}
        </div>
        <div v-if="heeftUitleg" :id="ids.help" class="form-text">{{ getTextFor(element.explanation,lang) }}</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">

import FormSubmitService from '../../services/FormSubmitService'
import veldToegankelijkheid from '../../utils/veldToegankelijkheid'
export default {
  mixins: [veldToegankelijkheid],
  name: 'FormElementCheckbox',
  props: ['element', 'lang', 'targetId','formData', 'wasValidated', 'hidden', 'formVersion'],
  data() {
    return {
      elData: {} as {[key: string]: boolean},
      selectOptions: [] as Array<{text: {[key: string]: string}, invalid: {[key: string]: string}, name: string, value: string}>,
      isLoading: false,
      formSubmitService: new FormSubmitService()
    }
  },
  watch: {
    'element.relationConfig': {
      handler() {
        this.loadOptions();
      },
      immediate: true
    }
  },
  computed: {
    isRequired() {
      return this.element.required && !Object.values(this.elData).some(v => v === true);
    }
  },
  methods: {
    /**
     * Eigen beoordeling in plaats van die van de mixin: een checkboxgroep is
     * geldig zodra er één optie is aangevinkt, en er is geen enkele control
     * waarop checkValidity() het antwoord geeft voor de groep als geheel.
     */
    herbeoordeel() {
      if (!this.ooitGevalideerd) { this.ongeldig = false; return; }
      this.ongeldig = !!this.element.required && !Object.values(this.elData).some(v => v === true);
    },
    checkboxChanged() {
      // Altijd array formaat gebruiken voor consistentie (net als AFormElementCheckbox)
      const selectedValues = Object.entries(this.elData)
        .filter(([, checked]) => checked)
        .map(([name]) => name);
      this.$emit('elementChanged', this.element.name, selectedValues);

      // Melding volgt de invoer, niet alleen de validatiepoging.
      this.herbeoordeel();
    },
    getTextFor(data: any, lang: string) {
      return data ? data[lang] : '';
    },
    checkFieldValidity(fieldRef: string) {
      const field = this.$refs[fieldRef];
      return field && field.checkValidity();
    },
    async loadOptions() {
      if (this.element.relationConfig && this.element.relationConfig.enabled) {
        this.isLoading = true;
        try {
          const { collectionPath, labelField, valueField, formConfigPath } = this.element.relationConfig;
          const relatedRecords = await this.formSubmitService.getRelatedRecords(
            collectionPath,
            labelField,
            valueField || 'id',
            formConfigPath
          );
          
          this.selectOptions = relatedRecords;
        } catch (error) {
          console.error('Fout bij het ophalen van gerelateerde records:', error);
          this.selectOptions = this.element.options || [];
        } finally {
          this.isLoading = false;
        }
      } else {
        this.selectOptions = this.element.options || [];
      }
    }
  },
  async created() {
    await this.loadOptions();
    if(this.formData[this.element.name]){
      if (this.formVersion >= 2 && Array.isArray(this.formData[this.element.name])) {
        // Nieuwe versie: convert array naar object structure
        const formValue = this.formData[this.element.name];
        this.elData = this.selectOptions.reduce((acc: any, option: any) => {
          acc[option.name] = formValue.includes(option.name);
          return acc;
        }, {});
      } else {
        // Oude versie: gebruik object direct
        this.elData = this.formData[this.element.name];
        // Vul missende opties aan met false
        for(let i in this.element.options) {
          const option = this.element.options[i];
          if(this.elData[option.name] === undefined){
            this.elData[option.name] = false;
          }
        }
      }
    } else {
      // Initialiseer leeg object
      this.elData = this.selectOptions.reduce((acc: any, option: any) => {
        acc[option.name] = false;
        return acc;
      }, {});
    }
  }
}
</script>

<style scoped>
.form-check-input {
  border-color: #ced4da;
}

/* Alleen validatie styling tonen als het formulier gevalideerd is */
.was-validated .form-check-input:required:invalid {
  border-color: #dc3545;
}

.was-validated .form-check-input:required:valid {
  border-color: #198754;
}
</style>

